import { ArrowRight, BadgeCheck, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Community } from '@/types';
import { ROUTES } from '@/config/constants';
import { Button } from '@/components/ui';

/** The official WINR community — gets the verified treatment. Matches how the
 *  rest of the app identifies it (communityView.ts, CuratedFeed). */
const WINR_OFFICIAL_ID = 'winr-official';

interface CommunityCardProps {
  community: Community;
  /** Real "trending" signal — members who joined in the last 24h. */
  dailyJoins?: number;
}

export function CommunityCard({ community, dailyJoins = 0 }: CommunityCardProps) {
  const navigate = useNavigate();

  const isFree = community.price_cents === 0;
  const priceText = isFree ? 'Free' : `$${(community.price_cents / 100).toFixed(0)}`;
  const priceCadence = isFree ? '' : ' / mo';

  const members = community.member_seq || 1;
  const membersText = members > 999 ? `${(members / 1000).toFixed(1).replace(/\.0$/, '')}K` : String(members);

  const description =
    community.description ||
    community.tagline ||
    'A focused space for members to connect, learn, and build momentum together.';

  const isVerified = community.id === WINR_OFFICIAL_ID;

  const open = () => navigate(ROUTES.community(community.id));

  return (
    <article
      className="group relative flex h-full cursor-pointer flex-col rounded-md bg-card p-3 transition-colors duration-200 hover:bg-elev after:pointer-events-none after:absolute after:-right-3 after:top-1/4 after:h-1/2 after:w-px after:bg-white/10 max-sm:after:hidden sm:max-lg:[&:nth-child(2n)]:after:hidden lg:max-xl:[&:nth-child(3n)]:after:hidden xl:[&:nth-child(4n)]:after:hidden"
      onClick={open}
    >
      {/* Thumbnail */}
      <div className="relative mb-3 aspect-[3/4] w-full flex-none overflow-hidden rounded-md bg-[#1a1a24]">
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-[800ms] ease-cinematic group-hover:scale-[1.05]"
          style={
            community.banner
              ? { backgroundImage: `url(${community.banner})` }
              : { background: `linear-gradient(140deg, ${community.color}66 0%, #1a1a24 100%)` }
          }
        />
        {/* Trending — members who joined in the last 24h, icon + number */}
        {dailyJoins > 0 && (
          <div className="absolute left-2.5 top-2.5 flex items-center gap-1.5 drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
            <TrendingUp size={16} strokeWidth={2.6} className="flex-none text-white drop-shadow-[0_0_6px_rgba(255,255,255,0.35)]" />
            <span className="animate-[shimmer_2s_ease-in-out_infinite] bg-[linear-gradient(110deg,#c0c0c0_35%,#ffffff_50%,#c0c0c0_65%)] bg-[length:200%_100%] bg-clip-text text-[16px] font-black tracking-[-0.3px] text-transparent">
              +{dailyJoins}
            </span>
          </div>
        )}
        {/* Price — electric-blue accent, black text */}
        <span className="absolute right-2.5 top-2.5 rounded-sm bg-accent px-2.5 py-1 text-[11px] font-bold text-black shadow-sm">
          {priceText}
          <span className="font-medium text-black/60">{priceCadence}</span>
        </span>
      </div>

      <h3 className="line-clamp-2 text-[18px] font-black leading-[1.15] tracking-[-0.5px] text-hi">
        {community.name}
      </h3>

      {isVerified && (
        <span className="mt-1.5 inline-flex w-fit items-center gap-1 rounded-pill bg-accent/15 px-2 py-0.5 text-[11px] font-bold text-accent">
          <BadgeCheck size={13} strokeWidth={2.5} className="fill-accent text-page" />
          Verified Community
        </span>
      )}

      <div className="mt-1.5 flex items-center gap-2 text-[13px] font-medium text-mid">
        <span>{membersText} members</span>
      </div>

      <p className="mt-2 line-clamp-2 text-[14px] leading-[1.45] text-lo">
        {description}
      </p>

      {/* Join + arrow — pinned to the bottom-right */}
      <div className="mt-auto flex items-center justify-end gap-3 pt-2.5">
        <Button
          variant="ghost"
          size="sm"
          className="px-0 text-hi shadow-none hover:bg-transparent hover:text-white"
          onClick={(e) => {
            e.stopPropagation();
            open();
          }}
        >
          Join
        </Button>
        <ArrowRight
          size={20}
          strokeWidth={2}
          className="text-lo transition-transform duration-300 ease-cinematic group-hover:translate-x-1 group-hover:text-hi"
        />
      </div>
    </article>
  );
}
